import { useState, useEffect, useCallback } from "react";
import { getAuth } from "firebase/auth";
import DateBox from "../crm/DateBox";
import { fmtD } from "../crm/dateFormat";

// URL of the SAME Apps Script used for Watchman PIN reset — this file
// just calls its "getUsage", "addPayment" and "deletePayment" actions.
const USAGE_DASHBOARD_URL = "https://script.google.com/macros/s/AKfycbxrzbBnegelyWenzq3yIdZeyhi2pHOZUjxsLUcycC2FSscPtYv6n6WfVcA49XGZzvG0/exec";

// Which Firebase projects to show, and which Google account owns each
// one. "id" must be the real GCP project ID (Firebase Console →
// Project Settings → Project ID) — not the display name.
// "billing: true" = this project is linked to "My Billing Account",
// so its cost is paid from the prepaid credit.
const PROJECTS = [
  { id: "abc-saas-prod", label: "abc-saas-prod (LIVE — real societies)", account: "layakarudu@gmail.com", billing: true },
  { id: "abc-saas-test", label: "abc-saas-test (testing)", account: "adyantam@gmail.com" },
  { id: "abc-dashboard001", label: "abc-dashboard (internal onboarding)", account: "layakarudu@gmail.com", billing: true },
  { id: "abc-demo-2fd7f", label: "abc-demo (client sandbox)", account: "adyantam@gmail.com" },
  { id: "abc-invoice-system", label: "abc-billing (Adyantam Invoice System)", account: "layakarudu@gmail.com", billing: true },
];

// Order accounts appear in on screen, and their display names.
const ACCOUNT_GROUPS = [
  { account: "adyantam@gmail.com", heading: "adyantam@gmail.com" },
  { account: "layakarudu@gmail.com", heading: "layakarudu@gmail.com" },
];

// Fallback USD → INR rate, only used if the live rate fetch fails.
const FALLBACK_USD_TO_INR = 95.3;
const EXCHANGE_RATE_URL = "https://api.frankfurter.dev/v1/latest?from=USD&to=INR";

function formatInr(usd, rate) {
  const inr = usd * rate;
  // Round to the nearest paisa first (as a number, not text), then strip
  // out -0 — Google's billing export sometimes returns a tiny negative
  // number (e.g. -0.000001) instead of a clean zero, which .toFixed(2)
  // would otherwise render as "-0.00".
  const rounded = Math.round(inr * 100) / 100;
  const clean = rounded === 0 ? 0 : rounded;
  return "₹" + clean.toFixed(2);
}

// Turns a { amount, currency } cost into rupees. If the billing export
// is already in INR it is used as is, otherwise converted from USD.
function costToInr(cost, rate) {
  if (!cost) return 0;
  return cost.currency === "INR" ? cost.amount : cost.amount * rate;
}

// Formats a rupee amount (already in INR), keeping the minus sign.
function formatRupee(inr) {
  const rounded = Math.round(inr * 100) / 100;
  const clean = rounded === 0 ? 0 : rounded;
  const sign = clean < 0 ? "-" : "";
  return sign + "₹" + Math.abs(clean).toFixed(2);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function PercentBar({ used, limit, label }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const over = used > limit;
  const barColor = over ? "#e05a5a" : pct > 75 ? "#e8a33d" : "#4ade80";
  return (
    <div style={{ width: "100%" }}>
      <div style={{
        display: "flex", justifyContent: "space-between",
        fontSize: "10px", color: over ? "#e05a5a" : "#8888a8", marginBottom: "2px",
      }}>
        <span>{label}</span>
        <span>{used.toLocaleString()} / {limit.toLocaleString()} ({pct}%){over && " over"}</span>
      </div>
      <div style={{
        width: "100%", height: "4px", background: "#2e2e40",
        borderRadius: "2px", overflow: "hidden",
      }}>
        <div style={{
          width: `${pct}%`, height: "100%", background: barColor,
          transition: "width 0.3s ease",
        }} />
      </div>
    </div>
  );
}

function ProjectUsageCardSkeleton({ label }) {
  const rows = ["Reads", "Writes", "Deletes"];
  return (
    <div style={{
      background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "8px",
      padding: "10px", width: "230px", boxSizing: "border-box", opacity: 0.55,
    }}>
      <div style={{ fontSize: "12px", fontWeight: 600, color: "#e8e8f0", marginBottom: "6px" }}>
        {label}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
        {rows.map((label) => (
          <div key={label} style={{ width: "100%" }}>
            <div style={{
              display: "flex", justifyContent: "space-between",
              fontSize: "10px", color: "#8888a8", marginBottom: "2px",
            }}>
              <span>{label}</span>
              <span>…</span>
            </div>
            <div style={{
              width: "100%", height: "4px", background: "#2e2e40", borderRadius: "2px",
            }} />
          </div>
        ))}
      </div>

      <div style={{ height: "1px", background: "#2e2e40", margin: "8px 0 6px" }} />

      <div style={{ fontSize: "10px", color: "#8888a8" }}>…</div>
    </div>
  );
}

function ProjectUsageCard({ result, rate, billing, startDate }) {
  if (result.error) {
    return (
      <div style={{
        background: "#1a1a24", border: "1px solid #ff525240", borderRadius: "8px",
        padding: "10px", width: "230px", boxSizing: "border-box",
      }}>
        <div style={{ fontSize: "12px", fontWeight: 600, color: "#e8e8f0", marginBottom: "4px" }}>
          {result.label || result.projectId}
        </div>
        <div style={{ fontSize: "11px", color: "#ff5252" }}>⚠ {result.error}</div>
      </div>
    );
  }

  const rows = [
    { key: "reads", label: "Reads" },
    { key: "writes", label: "Writes" },
    { key: "deletes", label: "Deletes" },
  ];
  const billed = result.billedCost;
  const sinceInr = costToInr(result.costSinceStart, rate);

  return (
    <div style={{
      background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "8px",
      padding: "10px", width: "230px", boxSizing: "border-box",
    }}>
      <div style={{ fontSize: "12px", fontWeight: 600, color: "#e8e8f0", marginBottom: "6px" }}>
        {result.label || result.projectId}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
        {rows.map(({ key, label }) => (
          <PercentBar
            key={key}
            label={label}
            used={result[key] ?? 0}
            limit={result.freeLimits?.[key] ?? 1}
          />
        ))}
      </div>

      <div style={{ height: "1px", background: "#2e2e40", margin: "8px 0 6px" }} />

      {billing ? (
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <div style={{
            fontSize: "14px", fontWeight: 700,
            color: sinceInr > 0 ? "#e8a33d" : "#4ade80",
          }}>
            {formatRupee(sinceInr)}
          </div>
          <div style={{ fontSize: "10px", color: "#8888a8" }}>
            {startDate ? `since ${fmtD(startDate)}` : "no payment date yet"}
          </div>
        </div>
      ) : billed ? (
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <div style={{
            fontSize: "14px", fontWeight: 700,
            color: billed.amount > 0 ? "#e8a33d" : "#4ade80",
          }}>
            {formatInr(billed.amount, rate)}
          </div>
          <div style={{ fontSize: "10px", color: "#8888a8" }}>
            {fmtD(billed.usageDate)}
          </div>
        </div>
      ) : (
        <div style={{ fontSize: "10px", color: "#8888a8" }}>
          Cost not available yet
        </div>
      )}
    </div>
  );
}

// ── Statement upload: reads payments/refunds from Google's monthly
// statement PDF (the "PAYMENTS RECEIVED" table). In Google's statement a
// payment is a NEGATIVE amount and a refund is POSITIVE.
const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };

async function parseStatementPdf(file) {
  const pdfjsLib = await import("pdfjs-dist/build/pdf");
  pdfjsLib.GlobalWorkerOptions.workerSrc = process.env.PUBLIC_URL + "/pdf.worker.min.js";
  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;

  // Rebuild text lines: items on (almost) the same height form one line.
  const lines = [];
  for (let n = 1; n <= pdf.numPages; n++) {
    const page = await pdf.getPage(n);
    const content = await page.getTextContent();
    const byY = [];
    content.items.forEach((it) => {
      if (!it.str || !it.str.trim()) return;
      const x = it.transform[4];
      const y = it.transform[5];
      let row = byY.find((r) => Math.abs(r.y - y) < 3);
      if (!row) {
        row = { y, items: [] };
        byY.push(row);
      }
      row.items.push({ x, str: it.str });
    });
    byY.sort((a, b) => b.y - a.y);
    byY.forEach((r) => {
      r.items.sort((a, b) => a.x - b.x);
      lines.push(r.items.map((i) => i.str).join(" ").replace(/\s+/g, " ").trim());
    });
  }

  const text = lines.join("\n");
  const yearMatch = text.match(/Summary for[^\n]*?(\d{4})/) || text.match(/Statement issue date[^\n]*?(\d{4})/);
  if (!yearMatch) throw new Error("not a statement");
  const year = yearMatch[1];

  const rows = [];
  lines.forEach((line) => {
    const m = line.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(.+?)\s+([-−]?[\d,]+\.\d{2})$/);
    if (!m || !/payment/i.test(m[3])) return;
    const month = MONTHS[m[2].toLowerCase()];
    if (!month) return;
    const signed = parseFloat(m[4].replace("−", "-").replace(/,/g, ""));
    if (!isFinite(signed) || signed === 0) return;
    rows.push({
      date: `${year}-${String(month).padStart(2, "0")}-${String(m[1]).padStart(2, "0")}`,
      kind: signed < 0 ? "payment" : "refund",
      amount: Math.abs(signed),
    });
  });
  return rows;
}

// ── Prepaid balance bar (compact) ───────────────────────────────────
// Balance = payments made − refunds − total cost since the first payment.
function BalanceBar({ data, rate, onAdd, onDelete, onImport, busy, formError }) {
  const [showPay, setShowPay] = useState(false);
  const [date, setDate] = useState(todayIso());
  const [amount, setAmount] = useState("");
  const [kind, setKind] = useState("payment");
  const [note, setNote] = useState("");
  const [importRows, setImportRows] = useState([]);
  const [importMsg, setImportMsg] = useState("");
  const [importBusy, setImportBusy] = useState(false);

  const ready = Boolean(data && data.projects);
  const payments = (data && data.payments) || [];
  const paid = payments.filter((p) => p.kind === "payment").reduce((s, p) => s + p.amount, 0);
  const refunded = payments.filter((p) => p.kind === "refund").reduce((s, p) => s + p.amount, 0);
  const netPaid = paid - refunded;

  const used = PROJECTS.filter((p) => p.billing).reduce((s, p) => {
    const r = ((data && data.projects) || []).find((x) => x.projectId === p.id);
    return s + costToInr(r?.costSinceStart, rate);
  }, 0);
  const balance = netPaid - used;
  const balanceColor = !ready ? "#8888a8" : balance < 0 ? "#e05a5a" : "#4ade80";

  const inputStyle = {
    background: "#0f0f13", border: "1px solid #2e2e40", borderRadius: "6px",
    color: "#e8e8f0", fontSize: "12px", padding: "6px 8px",
  };
  const labelStyle = { fontSize: "11px", color: "#8888a8", marginBottom: "2px" };
  const valueStyle = { fontSize: "16px", fontWeight: 700, color: "#e8e8f0" };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setImportBusy(true);
    setImportMsg("");
    setImportRows([]);
    try {
      const rows = await parseStatementPdf(file);
      setImportRows(rows);
      if (rows.length === 0) setImportMsg("No payments found in this file.");
    } catch (err) {
      setImportMsg("Couldn't read this file. Please choose the Google statement PDF.");
    }
    setImportBusy(false);
  };

  const previewRows = importRows.map((r) => ({
    ...r,
    exists: payments.some(
      (p) => p.date === r.date && p.kind === r.kind && Math.abs(p.amount - r.amount) < 0.005
    ),
  }));
  const newRows = previewRows.filter((r) => !r.exists);

  const confirmImport = async () => {
    const ok = await onImport(newRows);
    if (ok) {
      setImportRows([]);
      setImportMsg("Statement entries added.");
    }
  };

  const submit = async () => {
    const ok = await onAdd({ date, amount: Number(amount), kind, note });
    if (ok) {
      setAmount("");
      setNote("");
    }
  };

  return (
    <div style={{
      background: "#1a1a24", border: "1px solid #6c63ff40", borderRadius: "10px",
      padding: "12px 16px", marginBottom: "14px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "28px", flexWrap: "wrap" }}>
        <div>
          <div style={labelStyle}>Paid so far</div>
          <div style={valueStyle}>{ready ? formatRupee(netPaid) : "…"}</div>
        </div>
        <div>
          <div style={labelStyle}>Used{data?.startDate ? ` since ${fmtD(data.startDate)}` : ""}</div>
          <div style={valueStyle}>{ready ? formatRupee(used) : "…"}</div>
        </div>
        <div>
          <div style={labelStyle}>
            Balance{ready && balance < 0 ? " — arrange a payment" : ""}
          </div>
          <div style={{ fontSize: "22px", fontWeight: 700, color: balanceColor }}>
            {ready ? formatRupee(balance) : "…"}
          </div>
        </div>
        <div style={{ flex: 1, fontSize: "10px", color: "#8888a8", minWidth: "160px" }}>
          Shared Google Cloud credit: abc-saas-prod, abc-dashboard, abc-billing
        </div>
        <button
          type="button"
          onClick={() => setShowPay((v) => !v)}
          disabled={!ready}
          style={{
            background: "none", border: "1px solid #2e2e40", borderRadius: "8px",
            color: "#8888a8", fontSize: "12px", padding: "6px 12px",
            cursor: ready ? "pointer" : "not-allowed", opacity: ready ? 1 : 0.6,
          }}
        >
          {ready ? `Payments (${payments.length}) ${showPay ? "▲" : "▼"}` : "Payments"}
        </button>
      </div>

      {showPay && ready && (
        <div style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #2e2e40" }}>
          <div style={{ display: "flex", gap: "32px", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: "380px" }}>
          {payments.length === 0 && (
            <div style={{ fontSize: "12px", color: "#8888a8" }}>None added yet.</div>
          )}
          {payments.map((p) => (
            <div key={p.id} style={{
              display: "flex", alignItems: "center", gap: "10px",
              fontSize: "12px", color: "#e8e8f0", padding: "2px 0",
            }}>
              <span style={{ width: "100px", color: "#8888a8" }}>{fmtD(p.date)}</span>
              <span style={{ width: "62px", color: p.kind === "refund" ? "#e8a33d" : "#4ade80" }}>
                {p.kind === "refund" ? "Refund" : "Payment"}
              </span>
              <span style={{ width: "90px" }}>{formatRupee(p.amount)}</span>
              <span style={{ flex: 1, color: "#8888a8" }}>{p.note}</span>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  if (window.confirm("Delete this entry?")) onDelete(p.id);
                }}
                style={{
                  background: "none", border: "none", color: "#e05a5a",
                  fontSize: "11px", cursor: busy ? "not-allowed" : "pointer",
                }}
              >
                Delete
              </button>
            </div>
          ))}
          </div>

          <div style={{ flex: 1, minWidth: "380px" }}>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <DateBox value={date} onChange={setDate} style={{ ...inputStyle, width: inputStyle.width || 150 }} />
            <select value={kind} onChange={(e) => setKind(e.target.value)} style={inputStyle}>
              <option value="payment">Payment</option>
              <option value="refund">Refund</option>
            </select>
            <input
              type="number" min="0" step="0.01" placeholder="Amount ₹"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              style={{ ...inputStyle, width: "100px" }}
            />
            <input
              type="text" placeholder="Note (optional)" maxLength={80}
              value={note} onChange={(e) => setNote(e.target.value)}
              style={{ ...inputStyle, width: "160px" }}
            />
            <button
              type="button" onClick={submit} disabled={busy || !amount}
              style={{
                background: "#6c63ff", border: "none", borderRadius: "6px",
                color: "#fff", fontSize: "12px", padding: "6px 14px",
                cursor: busy || !amount ? "not-allowed" : "pointer",
                opacity: busy || !amount ? 0.6 : 1,
              }}
            >
              {busy ? "Saving…" : "Add"}
            </button>
          </div>
          <div style={{ marginTop: "12px", paddingTop: "10px", borderTop: "1px solid #2e2e40" }}>
            <label style={{
              display: "inline-block", border: "1px solid #2e2e40", borderRadius: "6px",
              color: "#8888a8", fontSize: "12px", padding: "6px 12px",
              cursor: importBusy || busy ? "not-allowed" : "pointer",
            }}>
              {importBusy ? "Reading…" : "Upload statement (PDF)"}
              <input
                type="file" accept="application/pdf" onChange={onFile}
                disabled={importBusy || busy} style={{ display: "none" }}
              />
            </label>
            {previewRows.map((r, i) => (
              <div key={i} style={{
                display: "flex", gap: "10px", fontSize: "12px", padding: "2px 0",
                color: r.exists ? "#8888a8" : "#e8e8f0",
              }}>
                <span style={{ width: "100px" }}>{fmtD(r.date)}</span>
                <span style={{ width: "62px" }}>{r.kind === "refund" ? "Refund" : "Payment"}</span>
                <span style={{ width: "90px" }}>{formatRupee(r.amount)}</span>
                <span>{r.exists ? "already added" : "new"}</span>
              </div>
            ))}
            {previewRows.length > 0 && (
              newRows.length > 0 ? (
                <button
                  type="button" onClick={confirmImport} disabled={busy}
                  style={{
                    marginTop: "6px", background: "#6c63ff", border: "none", borderRadius: "6px",
                    color: "#fff", fontSize: "12px", padding: "6px 14px",
                    cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1,
                  }}
                >
                  {busy ? "Saving…" : `Add ${newRows.length} new ${newRows.length === 1 ? "entry" : "entries"}`}
                </button>
              ) : (
                <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "4px" }}>
                  Everything in this statement is already added.
                </div>
              )
            )}
            {importMsg && (
              <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "4px" }}>{importMsg}</div>
            )}
          </div>
          {formError && (
            <div style={{ fontSize: "11px", color: "#ff5252", marginTop: "6px" }}>⚠ {formError}</div>
          )}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function FirestoreUsageDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exchangeRate, setExchangeRate] = useState(FALLBACK_USD_TO_INR);
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState("");

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError("You need to be signed in as a super admin to view this.");
        setLoading(false);
        return;
      }
      const idToken = await currentUser.getIdToken();

      const resp = await fetch(USAGE_DASHBOARD_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain" }, // deliberate — see Apps Script comment on CORS preflight
        body: JSON.stringify({
          action: "getUsage",
          idToken,
          targetProjectIds: PROJECTS.map((p) => p.id),
        }),
      });
      const json = await resp.json();

      if (!json.success) {
        setError(json.error || "Couldn't load usage data.");
        setData(null);
      } else {
        // Attach friendly labels to each result, matching PROJECTS order
        const withLabels = json.projects.map((r) => ({
          ...r,
          label: PROJECTS.find((p) => p.id === r.projectId)?.label,
        }));
        setData({
          date: json.date,
          projects: withLabels,
          payments: json.payments || [],
          startDate: json.startDate || null,
        });
      }
    } catch (err) {
      setError("Couldn't reach the usage service. Try again.");
    }
    setLoading(false);
  }, []);

  // Sends addPayment / deletePayment, then reloads the numbers.
  // Returns true on success.
  const sendPaymentAction = useCallback(async (payload) => {
    setPayBusy(true);
    setPayError("");
    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        setPayError("You need to be signed in as a super admin.");
        setPayBusy(false);
        return false;
      }
      const idToken = await currentUser.getIdToken();
      const resp = await fetch(USAGE_DASHBOARD_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify({ ...payload, idToken }),
      });
      const json = await resp.json();
      if (!json.success) {
        setPayError(json.error || "Couldn't save.");
        setPayBusy(false);
        return false;
      }
      await fetchUsage();
      setPayBusy(false);
      return true;
    } catch (err) {
      setPayError("Couldn't reach the usage service. Try again.");
      setPayBusy(false);
      return false;
    }
  }, [fetchUsage]);

  const handleAddPayment = (fields) => sendPaymentAction({ action: "addPayment", ...fields });
  const handleDeletePayment = (id) => sendPaymentAction({ action: "deletePayment", id });

  // Adds several entries read from a statement, then reloads once.
  const handleImport = useCallback(async (rows) => {
    setPayBusy(true);
    setPayError("");
    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        setPayError("You need to be signed in as a super admin.");
        setPayBusy(false);
        return false;
      }
      for (const r of rows) {
        const idToken = await currentUser.getIdToken();
        const resp = await fetch(USAGE_DASHBOARD_URL, {
          method: "POST",
          headers: { "Content-Type": "text/plain" },
          body: JSON.stringify({
            action: "addPayment", idToken,
            date: r.date, amount: r.amount, kind: r.kind, note: "From statement",
          }),
        });
        const json = await resp.json();
        if (!json.success) {
          setPayError(json.error || "Couldn't save.");
          await fetchUsage();
          setPayBusy(false);
          return false;
        }
      }
      await fetchUsage();
      setPayBusy(false);
      return true;
    } catch (err) {
      setPayError("Couldn't reach the usage service. Try again.");
      setPayBusy(false);
      return false;
    }
  }, [fetchUsage]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(EXCHANGE_RATE_URL);
        const json = await resp.json();
        if (json?.rates?.INR) setExchangeRate(json.rates.INR);
      } catch {
        // Fetch failed — keep the fallback rate already in state.
      }
    })();
  }, []);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
        <div>
          <div style={{ fontSize: "16px", fontWeight: 600, color: "#e8e8f0" }}>
            Firestore usage — today
          </div>
          {data?.date && (
            <div style={{ fontSize: "12px", color: "#8888a8", marginTop: "2px" }}>
              {fmtD(data.date)} · resets around midnight Pacific time
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={fetchUsage}
          disabled={loading}
          style={{
            background: "none", border: "1px solid #2e2e40", borderRadius: "8px",
            color: "#8888a8", fontSize: "12px", padding: "6px 12px",
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div style={{
          background: "#ff525215", border: "1px solid #ff525240", borderRadius: "10px",
          padding: "10px 14px", fontSize: "13px", color: "#ff5252", marginBottom: "12px",
        }}>
          ⚠ {error}
        </div>
      )}

      <BalanceBar
        data={data}
        rate={exchangeRate}
        onAdd={handleAddPayment}
        onDelete={handleDeletePayment}
        onImport={handleImport}
        busy={payBusy}
        formError={payError}
      />

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(480px, 1fr))",
        gap: "20px",
      }}>
        {ACCOUNT_GROUPS.map((group) => {
          const projectsInGroup = PROJECTS.filter((p) => p.account === group.account);
          if (projectsInGroup.length === 0) return null;

          const resultsById = {};
          if (data?.projects) {
            for (const r of data.projects) resultsById[r.projectId] = r;
          }

          const resultsInGroup = projectsInGroup
            .map((p) => resultsById[p.id])
            .filter(Boolean);
          const groupOnCredit = projectsInGroup.some((p) => p.billing);
          const groupTotal = resultsInGroup.reduce((sum, r) => sum + costToInr(r.costSinceStart, exchangeRate), 0);
          const groupHasData = resultsInGroup.length > 0;

          return (
            <div key={group.account}>
              <div style={{
                display: "flex", alignItems: "baseline", justifyContent: "space-between",
                marginBottom: "10px", paddingBottom: "6px",
                borderBottom: "1px solid #2e2e40",
              }}>
                <div style={{
                  fontSize: "12px", fontWeight: 600, color: "#8b7cff",
                  textTransform: "uppercase", letterSpacing: "0.04em",
                }}>
                  {group.heading}
                </div>
                <div style={{ fontSize: "13px", fontWeight: 700, color: "#8888a8" }}>
                  {!groupHasData ? "…" : groupOnCredit ? (
                    <span style={{ color: "#e8e8f0" }}>{formatRupee(groupTotal)}</span>
                  ) : (
                    "not on prepaid credit"
                  )}
                </div>
              </div>
              <div style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
              }}>
                {projectsInGroup.map((p) => {
                  const result = resultsById[p.id];
                  return result ? (
                    <ProjectUsageCard key={p.id} result={result} rate={exchangeRate} billing={Boolean(p.billing)} startDate={data?.startDate} />
                  ) : (
                    <ProjectUsageCardSkeleton key={p.id} label={p.label} />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: "11px", color: "#555570", marginTop: "8px" }}>
        Restricted to super admin accounts. Usage figures come directly from Google Cloud
        Monitoring — real usage, not an estimate. Cost figures are Google's own billed
        amounts (via Cloud Billing → BigQuery export), converted to INR at ~₹{exchangeRate.toFixed(2)}/USD.
        Billing export updates roughly once every 24 hours. Payments and refunds are typed in
        by the Owner (Google gives no way to read them automatically).
      </div>
    </div>
  );
}